import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, TrendingUp, TrendingDown, MessageSquare, BadgeCheck, ArrowLeft, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import StarRating from "../components/StarRating";
import Reveal, { SectionLabel } from "../components/Reveal";

export default function MemberProfile() {
  const { userId } = useParams();
  const { user, updateUser } = useAuth();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/members/${userId}`).then(({ data }) => setData(data)).catch(() => setNotFound(true));
  }, [userId]);

  const saveName = async () => {
    if (nameDraft.trim().length < 2) {
      toast.error("NAME MUST BE AT LEAST 2 CHARACTERS");
      return;
    }
    setSavingName(true);
    try {
      const { data: updated } = await api.put("/users/me", { name: nameDraft.trim() });
      updateUser(updated);
      setData((d) => ({ ...d, member: { ...d.member, name: updated.name } }));
      setEditingName(false);
      toast.success("PROFILE NAME UPDATED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  if (notFound)
    return (
      <div>
        <Header />
        <div className="mx-auto max-w-7xl px-5 py-32 text-center font-mono text-sm uppercase tracking-[0.25em] text-muted" data-testid="member-not-found">
          404 — MEMBER NOT IN THE CLUB
        </div>
        <Footer />
      </div>
    );

  if (!data)
    return (
      <div>
        <Header />
        <div className="mx-auto max-w-7xl px-5 py-32 font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="member-loading">
          LOADING PROFILE<span className="animate-blink">…</span>
        </div>
      </div>
    );

  const { member, certificates, reviews, journal } = data;
  const memberSince = new Date(member.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long" });

  return (
    <div data-testid="member-profile-page">
      <Header />
      <section className="relative overflow-hidden border-b border-line">
        <div className="terminal-grid absolute inset-0 opacity-50" />
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-signal/15 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <Link to="/firms" data-testid="member-back" className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-muted transition-colors hover:text-signal">
            <ArrowLeft size={12} /> Back to Index
          </Link>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap items-center gap-6">
            {member.picture ? (
              <img src={member.picture} alt="" className="h-20 w-20 rounded-2xl border border-line object-cover" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-line bg-elevate font-display text-3xl font-800 text-signal">
                {member.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-signal">{"// Club Member"}</p>
              {editingName ? (
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="member-name-editor">
                  <input
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    maxLength={60}
                    autoFocus
                    className="rounded-xl border border-signal/60 bg-ink px-4 py-2.5 font-display text-2xl font-800 text-white outline-none"
                    data-testid="member-name-input"
                  />
                  <button onClick={saveName} disabled={savingName}
                    className="btn-gradient inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-white disabled:opacity-50"
                    data-testid="member-name-save">
                    <Check size={12} /> {savingName ? "Saving…" : "Save"}
                  </button>
                  <button onClick={() => setEditingName(false)}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-line px-4 py-2.5 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-muted transition-colors hover:text-white"
                    data-testid="member-name-cancel">
                    <X size={12} /> Cancel
                  </button>
                </div>
              ) : (
                <h1 className="mt-1 flex flex-wrap items-center gap-3 font-display text-4xl font-900 tracking-tight text-white sm:text-5xl" data-testid="member-name">
                  {member.name}
                  {user && user.id === member.id && (
                    <button
                      onClick={() => { setNameDraft(member.name); setEditingName(true); }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line px-3.5 py-1.5 font-mono text-[9px] font-700 uppercase tracking-[0.15em] text-muted transition-colors hover:border-signal/60 hover:text-signal"
                      data-testid="member-edit-name-btn"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                </h1>
              )}
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
                Member since {memberSince} · {reviews.length} review{reviews.length === 1 ? "" : "s"} · {certificates.length} verified certificate{certificates.length === 1 ? "" : "s"}
              </p>
            </div>
            {certificates.length > 0 && (
              <span className="inline-flex items-center gap-2 rounded-full border border-signal/50 bg-signal/10 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.15em] text-signal" data-testid="member-verified-pill">
                <BadgeCheck size={14} /> Verified Payout Member
              </span>
            )}
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1fr_360px]">
          <div>
            {certificates.length > 0 && (
              <Reveal>
                <div data-testid="member-certificates">
                  <SectionLabel>Verified Payout Certificates</SectionLabel>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {certificates.map((c) => (
                      <Link key={c.id} to={`/firms/${c.firm_slug || ""}`} data-testid={`member-cert-${c.id}`}
                        className="card-violet flex items-center gap-4 p-5">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-signal/10">
                          <ShieldCheck size={20} className="text-signal" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-display text-base font-700 text-white">{c.firm_name}</p>
                          <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-signal">
                            {c.amount || "Payout verified"} · {new Date(c.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" })}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}

            <Reveal delay={0.08}>
              <div className="mt-12" data-testid="member-reviews">
                <SectionLabel>Firm Reviews</SectionLabel>
                <div className="mt-5 space-y-3.5">
                  {reviews.length === 0 && (
                    <p className="card-violet p-7 font-mono text-xs uppercase tracking-[0.2em] text-muted">No reviews filed yet.</p>
                  )}
                  {reviews.map((r) => (
                    <article key={r.id} className="card-violet p-6" data-testid={`member-review-${r.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <Link to={`/firms/${r.firm_slug}`} className="font-display text-lg font-700 text-white transition-colors hover:text-signal" data-testid={`member-review-firm-${r.id}`}>
                          {r.firm_name}
                        </Link>
                        <div className="flex items-center gap-3">
                          {r.verified_payout && (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-signal/50 bg-signal/10 px-3 py-1 font-mono text-[9px] font-700 uppercase tracking-[0.12em] text-signal">
                              <ShieldCheck size={11} /> Verified Payout
                            </span>
                          )}
                          <StarRating value={r.rating} idPrefix={`m-${r.id.slice(0, 6)}`} size={13} />
                        </div>
                      </div>
                      {r.title && <h3 className="mt-3 font-display text-base font-700 text-white">{r.title}</h3>}
                      <p className="mt-1.5 text-sm leading-relaxed text-muted">{r.comment}</p>
                      <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-muted">
                        {new Date(r.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                      </p>
                    </article>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="card-violet p-7" data-testid="member-journal-stats">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-signal">{"// Journal Stats"}</p>
              {journal ? (
                <div className="mt-5 space-y-4">
                  <div className="flex items-center justify-between border-b border-line/60 pb-3.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Trades Logged</span>
                    <span className="font-mono text-lg font-700 text-white">{journal.count}</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-line/60 pb-3.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Win Rate</span>
                    <span className={`font-mono text-lg font-700 ${journal.win_rate >= 50 ? "text-signal" : "text-gold"}`}>{journal.win_rate}%</span>
                  </div>
                  <div className="flex items-center justify-between border-b border-line/60 pb-3.5">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Net P&L</span>
                    <span className={`inline-flex items-center gap-1.5 font-mono text-lg font-700 ${journal.net_pnl >= 0 ? "text-signal" : "text-loss"}`}>
                      {journal.net_pnl >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />}
                      {journal.net_pnl >= 0 ? "+" : ""}${journal.net_pnl.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Best Trade</span>
                    <span className="font-mono text-lg font-700 text-signal">+${journal.best_trade.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p className="mt-4 text-sm text-muted" data-testid="member-no-journal">No journal entries yet.</p>
              )}
            </div>
            <div className="card-violet p-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted">{"// Trust Signal"}</p>
              <p className="mt-4 text-sm leading-relaxed text-muted">
                {certificates.length > 0
                  ? "This member's payouts were verified against real screenshots by the club desk. Their reviews carry extra weight."
                  : "Reviews from this member are tied to a real club account. Certificates appear here once verified."}
              </p>
            </div>
          </aside>
        </div>
      </section>
      <Footer />
    </div>
  );
}
